import api from '../api';
import secureStorage from '../../utils/SecureStorage';

// Conforme a documentação da API:
// GET all: /appv6/cadastros/hiperlink/all -> REACT_APP_API_BASE_76
// CRUD:    /appv6/cadastros/hiperlink      -> REACT_APP_API_BASE_77
const API_URL_GET_ALL = process.env.REACT_APP_API_BASE_76;
const API_URL_CRUD = process.env.REACT_APP_API_BASE_77;

/**
 * Helper para obter o idRede e lançar um erro se não for encontrado.
 * @returns {string} O ID da rede.
 * @throws {Error} Se o ID da rede não for encontrado.
 */
const getIdRedeOrThrow = () => {
  const idRede = secureStorage.getItem("id_r");
  if (!idRede) {
    const errorMessage = "ID da rede não encontrado. Selecione uma rede para continuar.";
    console.error(errorMessage);
    alert(errorMessage); // Mantendo o padrão de alerta, mas um sistema de notificação é melhor.
    throw new Error(errorMessage);
  }
  return idRede;
};

/**
 * Processa a resposta da API, extrai a lista de itens e normaliza o _id.
 * @param {object} data - O objeto de dados da resposta da API.
 * @returns {Array} Uma lista de itens processados.
 */
const processApiResponse = (data) => {
  // A API pode retornar a lista em 'hiperlinks', 'cadastros' ou na raiz.
  const items = data?.hiperlinks || data?.cadastros || (Array.isArray(data) ? data : []);

  if (!Array.isArray(items)) {
    return [];
  }

  // Normaliza o _id para ser uma string, lidando com o formato { $oid: '...' }
  return items.map(item => ({
    ...item,
    _id: item._id?.$oid || item._id,
  }));
};

const HiperlinkService = {
  /**
   * Busca todos os hiperlinks associados a uma rede.
   * Método: GET, URL: /cadastros/hiperlink/all
   * @returns {Promise<Array>} Uma lista de hiperlinks.
   */
  getHiperlinks: async () => {
    const idRede = getIdRedeOrThrow();
    try {
      // A documentação indica /all, mas é provável que a API filtre por id_rede via query param.
      const response = await api.get(`${API_URL_GET_ALL}?id_rede=${idRede}`);
      // Processa a resposta para garantir um formato consistente
      return processApiResponse(response.data);
    } catch (error) {
      console.error("Erro ao buscar hiperlinks:", error);
      throw new Error("Não foi possível buscar os hiperlinks. Tente novamente mais tarde.");
    }
  },

  /**
   * Busca um hiperlink por ID.
   * Método: GET, URL: /cadastros/hiperlink/{id}
   * @param {string} hiperlinkId - O ID do hiperlink.
   * @returns {Promise<object>} O objeto do hiperlink.
   */
  getHiperlinkById: async (hiperlinkId) => {
    getIdRedeOrThrow(); // Garante que o usuário está no contexto de uma rede.
    try {
      const response = await api.get(`${API_URL_CRUD}/${hiperlinkId}`);
      return response.data;
    } catch (error) {
      console.error(`Erro ao buscar o hiperlink com ID ${hiperlinkId}:`, error);
      throw new Error("Não foi possível buscar o hiperlink especificado.");
    }
  },

  /**
   * Filtra hiperlinks por campos.
   * Método: GET, URL: /cadastros/hiperlink/fields?tipo=app
   * @param {object} filters - Objeto com os filtros. Ex: { tipo: 'app' }
   * @returns {Promise<Array>} Uma lista de hiperlinks filtrados.
   */
  getHiperlinksByFilter: async (filters) => {
    const idRede = getIdRedeOrThrow();
    const queryParams = new URLSearchParams(filters).toString();
    try {
      // Adiciona id_rede aos filtros para a busca na API.
      const response = await api.get(`${API_URL_CRUD}/fields?${queryParams}`);
      // Processa a resposta para garantir um formato consistente
      return processApiResponse(response.data);
    } catch (error) {
      console.error("Erro ao filtrar hiperlinks:", error);
      throw new Error("Não foi possível filtrar os hiperlinks.");
    }
  },

  /**
   * Cria um novo hiperlink.
   * Método: POST, URL: /cadastros/hiperlink
   * @param {object} hiperlinkData - Os dados do hiperlink a serem salvos.
   */
  createHiperlink: async (hiperlinkData) => {
    const idRede = getIdRedeOrThrow();
    const dataToSave = {
      ...hiperlinkData,
      id_rede: idRede,
    };

    try {
      const response = await api.post(API_URL_CRUD, dataToSave);
      alert("Hiperlink salvo com sucesso!");
      return response.data;
    } catch (error) {
      console.error("Erro ao salvar o hiperlink:", error);
      alert("Erro ao salvar o hiperlink. Verifique o console para mais detalhes.");
      throw error;
    }
  },

  /**
   * Atualiza um hiperlink existente.
   * Método: PUT, URL: /cadastros/hiperlink/{id}
   * @param {object} hiperlinkData - Os dados do hiperlink, incluindo o _id.
   */
  updateHiperlink: async (hiperlinkData) => {
    getIdRedeOrThrow(); // Garante que o usuário está no contexto de uma rede.
    const { _id, ...dataToUpdate } = hiperlinkData;
    if (!_id) {
        const errorMessage = "Erro ao editar: ID do hiperlink (_id) não fornecido.";
        alert(errorMessage);
        console.error(errorMessage);
        throw new Error(errorMessage);
    }

    const updatedData = {
        ...dataToUpdate,
        data_update: new Date().toISOString(),
    };

    try {
      // A API deve garantir que o usuário só pode atualizar hiperlinks da sua própria rede.
      const response = await api.put(`${API_URL_CRUD}/${_id}`, updatedData);
      alert("Hiperlink editado com sucesso!");
      return response.data;
    } catch (error) {
      console.error("Erro ao editar o hiperlink:", error);
      alert("Erro ao editar o hiperlink. Verifique o console para mais detalhes.");
      throw error;
    }
  },

  /**
   * Deleta um hiperlink.
   * Método: DELETE, URL: /cadastros/hiperlink/{id}
   * @param {string} hiperlinkId - O ID do hiperlink a ser deletado.
   */
  deleteHiperlink: async (hiperlinkId) => {
    getIdRedeOrThrow(); // Garante que o usuário está no contexto de uma rede.
    try {
      // A API deve garantir que o usuário só pode deletar hiperlinks da sua própria rede.
      await api.delete(`${API_URL_CRUD}/${hiperlinkId}`);
      alert("Hiperlink deletado com sucesso!");
    } catch (error) {
      console.error("Erro ao deletar o hiperlink:", error);
      alert("Erro ao deletar o hiperlink. Verifique o console para mais detalhes.");
      throw error;
    }
  },
};

export default HiperlinkService;
